import { MapIcon } from '@/types/canvas';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Trash2, ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { MapIcon as MapIconType } from '@/lib/api';
import { getIconColor } from '@/utils/iconUtils';
import { iconDataCache } from '@/utils/iconDataCache';
import { useTranslation } from 'react-i18next';
import { IconColorPicker } from './IconColorPicker';

interface MapIconEditorProps {
  icon: MapIcon | null;
  onUpdate: (id: string, updates: Partial<MapIcon>) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}

export function MapIconEditor({ icon, onUpdate, onDelete, onClose }: MapIconEditorProps) {
  const { t } = useTranslation();
  const [iconData, setIconData] = useState<MapIconType | null>(null);
  const [loading, setLoading] = useState(false);

  // Načíst iconData ze Supabase pro získání default_width/height (ne pro barvu)
  useEffect(() => {
    if (!icon) {
      setIconData(null);
      return;
    }

    const loadIconData = async () => {
      setLoading(true);
      const iconData = await iconDataCache.get(icon.iconId);
      setIconData(iconData);
      setLoading(false);
    };

    loadIconData();
  }, [icon?.iconId]);

  if (!icon) {
    return (
      <div className="p-4 flex items-center justify-center h-full">
        <p className="text-muted-foreground">{t('settings.iconEditor.selectIconFirst')}</p>
      </div>
    );
  }

  // Získat aktuální barvu pomocí jednotné logiky - default je "black"
  const currentColor = getIconColor(icon, iconData);
  // Použij currentColor (vždy vrátí validní barvu, defaultně "black")

  // Použij currentColor (vždy vrátí validní barvu, defaultně "black")
  const displayColor = currentColor;


  const handleChange = (updates: Partial<MapIcon>) => {
    // if (import.meta.env.DEV && updates.color !== undefined) {
    //   console.log(`[MapIconEditor] handleChange called: iconId=${icon.id}, color=${updates.color}`);
    // }
    onUpdate(icon.id, updates);
  };

  const currentWidth = icon.width || iconData?.default_width || 40;
  // Původní velikost ikony pro výpočet maximální velikosti (5x původní)
  const originalWidth = iconData?.default_width || 40;
  const maxWidth = originalWidth * 5;
  const minWidth = 20;

  return (
    <div className="flex flex-col h-full">
      <div className="space-y-6">
        {/* Back button */}
        <Button
          variant="outline"
          onClick={onClose}
          className="w-full mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('settings.iconEditor.backToAddIcons') || 'Zpět na přidávání ikon'}
        </Button>

        {/* Size */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-base font-semibold">{t('settings.iconEditor.size')}</Label>
            <span className="text-sm text-muted-foreground">{currentWidth}px</span>
          </div>
          <Slider
            value={[currentWidth]}
            onValueChange={(values) => {
              const newSize = values[0];
              handleChange({ width: newSize, height: newSize });
            }}
            min={minWidth}
            max={maxWidth}
            step={5}
          />
          <div className="flex justify-between text-xs text-muted-foreground px-1">
            <span>{minWidth}px</span>
            <span>{maxWidth}px</span>
          </div>
        </div>

        {/* Color */}
        {/* Color */}
        <div className="space-y-3">
          {loading ? (
            <p className="text-xs text-muted-foreground">{t('common.loading')}</p>
          ) : (
            <div className="space-y-2">
              <IconColorPicker
                label={t('settings.iconEditor.color')}
                color={displayColor}
                onChange={(newColor) => handleChange({ color: newColor })}
              />
              {icon.color !== undefined && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleChange({ color: undefined })}
                  className="text-xs w-full"
                >
                  {t('settings.iconEditor.resetToDefault')}
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Delete button */}
        <div className="pt-4 border-t">
          <Button
            variant="destructive"
            onClick={() => {
              onDelete(icon.id);
              onClose();
            }}
            className="w-full"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {t('settings.iconEditor.deleteIcon')}
          </Button>
        </div>
      </div>
    </div>
  );
}

